import { useEffect, useRef, useState } from "react";
import {
  AGENT_BRIDGE_PROTOCOL_VERSION,
  AGENT_COMMANDS,
  AGENT_EVENTS,
  AgentBridgeError,
  createAgentBridge,
  findTemplate,
  getAgentHost,
  toAgentTemplates,
} from "./agentBridge";
import { ANATHOMY_UNKNOWN, expectedAnathomyCode } from "../templateAnathomy";
import { DICTATION_LANGUAGES } from "../../constants/languages";

export const AGENT_HOST_READY_EVENT = "creAgentHostReady";
/** Max wait for the page to apply dictation/template before Process AI starts. */
export const PROCESS_AI_APPLY_TIMEOUT_MS = 5000;
const REPORT_LANGUAGES = new Set(["en", "he", "ar", "fr", "es"]);
const DICTATION_LANGUAGE_CODES = new Set(DICTATION_LANGUAGES.map((l) => l.value));

/**
 * Connects the CRE patient page (Agent Mode only) to the Windows agent.
 * All handlers reuse the page's own CRE functions — template select, Process AI,
 * report copy payload, reset — so behaviour is exactly CRE's.
 *
 * @param {object} p
 * @param {boolean} p.enabled              Agent Mode (CRE + ?agent=1)
 * @param {Array}   p.templates            CRE templates (sidebar list)
 * @param {*}       p.selectedTemplateId
 * @param {string}  [p.selectedAnathomyCode] Anatomy code the page currently has ("" = none);
 *                                       Process AI waits until it matches the template.
 * @param {boolean} [p.noTemplateMode]   CRE "General Report / No Template" toggle (Hebrew).
 * @param {Function} [p.setNoTemplateMode] Sets that toggle (CRE users only). Lets the agent
 *                                       run Process AI exactly as if the box were ticked.
 * @param {string}  p.sidebarTranscript    CRE findings text (Process AI input)
 * @param {Function} p.setSidebarTranscript
 * @param {Function} p.selectTemplate      CRE handleTemplateSelect(template)
 * @param {Function} p.processWithAI       CRE handleProcessWithAI() → { ok, mirrorMessage }
 * @param {boolean} p.isAiLoading
 * @param {Function} p.getReport           () → { html, plainText } (CRE copy payload)
 * @param {Function} p.newPatient          async reset of report + dictation
 * @param {string}  p.reportLanguage
 * @param {Function} p.setReportLanguage
 * @param {Array}   p.organisations        [{ organisationId, name }]
 * @param {*}       p.selectedOrganisationId
 * @param {Function} p.setOrganisation
 * @param {Function} [p.reloadCatalog]     refetch templates + macros (after edits in another CRE window)
 * @param {Function} [p.stopEditorMic]     stop the report editor mic → true if it was on
 * @param {string}  [p.dictationLanguage] CRE editor mic language (e.g. "he-IL")
 * @param {Function} [p.setDictationLanguage] set by the agent: its own dictation language
 */
export function useCreAgentBridge(p) {
  const latest = useRef(p);
  latest.current = p;

  const [host, setHost] = useState(() => (p.enabled ? getAgentHost() : null));
  const [tick, setTick] = useState(0);
  const bridgeRef = useRef(null);
  const pendingRef = useRef(null);
  const aiDoneRef = useRef(false);

  // The host normally exists before the page loads (agent preload); a test
  // harness can define it later and dispatch "creAgentHostReady".
  useEffect(() => {
    if (!p.enabled) return undefined;
    const check = () => setHost(getAgentHost());
    check();
    window.addEventListener(AGENT_HOST_READY_EVENT, check);
    return () => window.removeEventListener(AGENT_HOST_READY_EVENT, check);
  }, [p.enabled]);

  const buildState = () => {
    const L = latest.current;
    return {
      protocolVersion: AGENT_BRIDGE_PROTOCOL_VERSION,
      templates: toAgentTemplates(L.templates),
      selectedTemplateId: L.selectedTemplateId == null ? null : String(L.selectedTemplateId),
      isAiLoading: !!L.isAiLoading,
      aiProcessed: aiDoneRef.current,
      reportLanguage: L.reportLanguage || null,
      dictationLanguage: L.dictationLanguage || null,
      organisations: Array.isArray(L.organisations) ? L.organisations : [],
      selectedOrganisationId: L.selectedOrganisationId ?? null,
      noTemplateMode: !!L.noTemplateMode,
      // The agent may send processAi { noTemplate: true } (CRE's No Template mode).
      noTemplateSupported: typeof L.setNoTemplateMode === "function",
    };
  };

  useEffect(() => {
    if (!p.enabled || !host) return undefined;
    const handlers = {
      [AGENT_COMMANDS.GET_STATE]: () => buildState(),

      [AGENT_COMMANDS.SET_TEMPLATE]: ({ templateId }) => {
        const L = latest.current;
        const template = findTemplate(L.templates, templateId);
        if (!template) throw new AgentBridgeError("unknown_template", `Template not found: ${templateId}`);
        L.selectTemplate(template);
        return { templateId: String(template.templateId) };
      },

      [AGENT_COMMANDS.SET_REPORT_LANGUAGE]: ({ language }) => {
        const lang = String(language || "").toLowerCase().split(/[-_]/)[0];
        if (!REPORT_LANGUAGES.has(lang)) throw new AgentBridgeError("bad_language", `Unsupported language: ${language}`);
        latest.current.setReportLanguage(lang);
        return { language: lang };
      },

      [AGENT_COMMANDS.SET_ORGANISATION]: ({ organisationId }) => {
        const L = latest.current;
        const known = (L.organisations || []).some((o) => String(o.organisationId) === String(organisationId));
        if (!known) throw new AgentBridgeError("unknown_organisation", `Organisation not found: ${organisationId}`);
        L.setOrganisation(organisationId);
        return { organisationId };
      },

      [AGENT_COMMANDS.PROCESS_AI]: ({ dictation, templateId, noTemplate }) => {
        const L = latest.current;
        if (typeof dictation !== "string" || !dictation.trim()) {
          throw new AgentBridgeError("empty_dictation", "Nothing dictated");
        }
        if (L.isAiLoading || pendingRef.current) throw new AgentBridgeError("busy", "Process AI already running");
        // noTemplate: process like CRE's "General Report / No Template" box is
        // ticked (Jacky/Vigen 2026-10-29: no template chosen in the agent).
        const wantNoTemplate = noTemplate === true;
        if (wantNoTemplate && templateId != null) {
          throw new AgentBridgeError("bad_request", "noTemplate and templateId are exclusive");
        }
        const canSetMode = typeof L.setNoTemplateMode === "function";
        if (wantNoTemplate && !canSetMode) {
          throw new AgentBridgeError("unsupported", "No Template mode is not available");
        }
        // The page's box follows each agent request, so an agent template is
        // never overridden by a box left ticked from an earlier request.
        if (canSetMode) L.setNoTemplateMode(wantNoTemplate);
        if (templateId != null) {
          const template = findTemplate(L.templates, templateId);
          if (!template) throw new AgentBridgeError("unknown_template", `Template not found: ${templateId}`);
          // Always (re)load the agent's template, even when CRE already shows the
          // same one: the report is built from this template + the agent's
          // dictation only, never from whatever the editor held before
          // (a previous report, another patient's findings, a restored draft).
          L.selectTemplate(template);
        }
        L.setSidebarTranscript(dictation);
        // Process AI runs once React has applied the dictation/template (effect below).
        return new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            if (pendingRef.current?.timer === timer) {
              pendingRef.current = null;
              reject(new AgentBridgeError("timeout", "Dictation/template were not applied"));
            }
          }, PROCESS_AI_APPLY_TIMEOUT_MS);
          pendingRef.current = {
            dictation,
            templateId: templateId == null ? null : String(templateId),
            noTemplate: canSetMode ? wantNoTemplate : null,
            resolve,
            reject,
            timer,
          };
          setTick((n) => n + 1);
        });
      },

      [AGENT_COMMANDS.GET_REPORT]: () => {
        const { html = "", plainText = "" } = latest.current.getReport() || {};
        return { html, plainText };
      },

      // The agent's "Templates & macros" window closed: templates/macros may have changed.
      [AGENT_COMMANDS.RELOAD_CATALOG]: async () => {
        await latest.current.reloadCatalog?.();
        return null;
      },

      // The agent left the report view: the editor mic must not keep listening
      // behind the agent's own dictation view.
      [AGENT_COMMANDS.STOP_EDITOR_MIC]: () => ({ stopped: !!latest.current.stopEditorMic?.() }),

      // The editor mic (dictation and AI Edit) speaks the agent's language: the
      // agent sends its dictation language on connect and whenever it changes.
      [AGENT_COMMANDS.SET_DICTATION_LANGUAGE]: ({ language } = {}) => {
        const code = String(language || "");
        if (!DICTATION_LANGUAGE_CODES.has(code)) {
          throw new AgentBridgeError("bad_language", `Unsupported dictation language: ${language}`);
        }
        latest.current.setDictationLanguage?.(code);
        return { language: code };
      },

      [AGENT_COMMANDS.NEW_PATIENT]: async () => {
        pendingRef.current = null;
        aiDoneRef.current = false;
        await latest.current.newPatient();
        return null;
      },
    };
    const bridge = createAgentBridge(host, handlers);
    bridgeRef.current = bridge;
    bridge.emit(AGENT_EVENTS.READY, buildState());
    return () => {
      bridge.dispose();
      if (bridgeRef.current === bridge) bridgeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.enabled, host]);

  // Run a pending Process AI once the page shows the agent's dictation/template.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    const L = latest.current;
    if (L.sidebarTranscript !== pending.dictation) return;
    // Wait until the page has applied the No Template box as requested.
    if (pending.noTemplate != null && !!L.noTemplateMode !== pending.noTemplate) return;
    if (pending.templateId != null) {
      if (String(L.selectedTemplateId) !== pending.templateId) return;
      // CRE derives the template's anatomy (speech dictionary + AI prompt) in a
      // later render; run only once the page has caught up, exactly like a
      // user who selects a template and then clicks Process AI.
      const expected = expectedAnathomyCode({ templateId: pending.templateId, templates: L.templates });
      if (expected !== ANATHOMY_UNKNOWN && (L.selectedAnathomyCode || "") !== expected) return;
    }
    pendingRef.current = null;
    clearTimeout(pending.timer);
    const bridge = bridgeRef.current;
    bridge?.emit(AGENT_EVENTS.PROCESS_AI, { phase: "started" });
    Promise.resolve()
      .then(() => L.processWithAI())
      .then((res) => {
        const ok = !!res?.ok;
        if (ok) aiDoneRef.current = true;
        const report = ok ? latest.current.getReport() || { html: "", plainText: "" } : null;
        bridge?.emit(AGENT_EVENTS.PROCESS_AI, { phase: "finished", ok });
        pending.resolve({ ok, message: res?.mirrorMessage || null, report });
      })
      .catch((err) => {
        bridge?.emit(AGENT_EVENTS.PROCESS_AI, { phase: "finished", ok: false });
        pending.reject(err);
      });
  }, [p.sidebarTranscript, p.selectedTemplateId, p.selectedAnathomyCode, p.noTemplateMode, tick]);

  // Tell the agent when templates / selection / language / organisation / loading change.
  const signature = JSON.stringify([
    toAgentTemplates(p.templates).map((t) => t.templateId),
    p.selectedTemplateId,
    !!p.isAiLoading,
    p.reportLanguage,
    p.dictationLanguage,
    p.selectedOrganisationId,
    (p.organisations || []).length,
    !!p.noTemplateMode,
  ]);
  useEffect(() => {
    bridgeRef.current?.emit(AGENT_EVENTS.STATE, buildState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, host]);
}
